/**
 * Shared primary/secondary navigation for configuration blades.
 *
 * Contains:
 * - ConfigurationBladeTabs — scrollable labeled tab strip
 */
import { Box, Tab, Tabs, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import React from 'react';

const tabsRegionSx = {
  flexShrink: 0,
  bgcolor: 'background.paper',
  borderBottom: '1px solid var(--border-default)',
};

const tabsSx = {
  minHeight: 56,
  px: 'var(--page-gap)',
};

const tabSx = {
  minHeight: 56,
  minWidth: 128,
  maxWidth: 200,
  px: 'var(--space-3)',
  py: 'var(--space-2)',
  alignItems: 'stretch',
  textTransform: 'none',
  '& .ConfigurationBladeTabLabel-primary': { color: 'text.primary' },
  '& .ConfigurationBladeTabLabel-secondary': { color: 'text.secondary' },
  '&.Mui-selected .ConfigurationBladeTabLabel-primary': { color: 'primary.main' },
};

function ConfigurationBladeTabLabel({ primary, secondary }) {
  return (
    <Box sx={{ minWidth: 0, width: '100%', textAlign: 'left' }}>
      <Typography
        component="span"
        variant="body2"
        className="ConfigurationBladeTabLabel-primary"
        noWrap
        sx={{ display: 'block', fontWeight: 600, lineHeight: 1.25 }}
      >
        {primary}
      </Typography>
      <Typography
        component="span"
        variant="caption"
        className="ConfigurationBladeTabLabel-secondary"
        noWrap
        sx={{ display: 'block', lineHeight: 1.25 }}
      >
        {secondary}
      </Typography>
    </Box>
  );
}

ConfigurationBladeTabLabel.propTypes = {
  primary: PropTypes.string.isRequired,
  secondary: PropTypes.string.isRequired,
};

/**
 * @param {object} props
 * @param {Array<{key: string, value: number|string, primary: string, secondary: string}>} props.tabs
 * @param {number|string} props.value
 * @param {Function} props.onChange
 * @param {string} props.ariaLabel
 * @returns {React.ReactElement} Scrollable configuration navigation.
 */
export default function ConfigurationBladeTabs({ tabs, value, onChange, ariaLabel }) {
  return (
    <Box sx={tabsRegionSx}>
      <Tabs
        value={value}
        onChange={onChange}
        sx={tabsSx}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        aria-label={ariaLabel}
      >
        {tabs.map(tab => (
          <Tab
            key={tab.key}
            value={tab.value}
            label={<ConfigurationBladeTabLabel primary={tab.primary} secondary={tab.secondary} />}
            sx={tabSx}
          />
        ))}
      </Tabs>
    </Box>
  );
}

ConfigurationBladeTabs.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      primary: PropTypes.string.isRequired,
      secondary: PropTypes.string.isRequired,
    }),
  ).isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  onChange: PropTypes.func.isRequired,
  ariaLabel: PropTypes.string.isRequired,
};
